import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck, FileSearch } from 'lucide-react';

const iconStroke = 1.75;

const proofCases = [
  {
    slug: 'australia-post-catalogue',
    title: 'Catalogue remediation',
    challenge: 'Ungridded product cells, repeated images, prices and stock codes.',
    result: 'PASS certificate, 100/100 score, 193 images described.',
    sourceImage: '/case-studies/australia-post-catalogue/source-page-07.jpg',
    certificateImage: '/case-studies/certificates/australia-post-catalogue.png',
    sourceAlt:
      'Original catalogue PDF page with product images, descriptions, stock codes and prices in a free-form layout.',
    certificateAlt: 'PDF AA Compliance certificate for the Australia Post catalogue remediation case study.',
  },
  {
    slug: 'bass-coast-planning',
    title: 'Map-heavy planning document',
    challenge: 'Maps, legends, place labels, links and long civic-policy content.',
    result: 'PASS certificate, 100/100 score, 121 images described.',
    sourceImage: '/case-studies/bass-coast-planning/source-page-05.jpg',
    certificateImage: '/case-studies/certificates/bass-coast-planning.png',
    sourceAlt:
      'Original planning PDF page containing a map with labels, boundaries and dense surrounding information.',
    certificateAlt: 'PDF AA Compliance certificate for the Bass Coast planning remediation case study.',
  },
  {
    slug: 'cfa-medical-standards',
    title: 'Long technical standards',
    challenge: 'Deep headings, tables, flowchart pages and long-document consistency.',
    result: 'PASS certificate, 100/100 score, 51 tables with headers.',
    sourceImage: '/case-studies/cfa-medical-standards/source-page-08.jpg',
    certificateImage: '/case-studies/certificates/cfa-medical-standards.png',
    sourceAlt:
      'Original long-form PDF standards page with structured content and table-like information.',
    certificateAlt: 'PDF AA Compliance certificate for the CFA medical standards remediation case study.',
  },
];

type ProofShowcaseProps = {
  eyebrow?: string;
  title?: string;
  description?: string;
  className?: string;
  band?: boolean;
  compact?: boolean;
};

export default function ProofShowcase({
  eyebrow = 'Real document proof',
  title = 'See the source complexity and the certificate outcome together.',
  description = 'The strongest proof is visual: the original document page shows the remediation challenge, and the certificate shows the verified outcome after processing.',
  className = '',
  band = false,
  compact = false,
}: ProofShowcaseProps) {
  return (
    <section className={`${band ? 'bg-slate-800' : 'bg-slate-900'} px-4 py-14 sm:px-6 lg:px-8 ${className}`}>
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-300">
              {eyebrow}
            </p>
            <h2 className="mt-3 text-3xl font-semibold leading-tight text-white">{title}</h2>
            <p className="mt-4 text-base leading-7 text-slate-300">{description}</p>
          </div>
          <Link
            to="/case-studies"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary-300 hover:text-primary-200"
          >
            View full case studies
            <ArrowRight className="h-4 w-4" strokeWidth={iconStroke} />
          </Link>
        </div>

        <div className={`mt-9 grid gap-5 ${compact ? 'lg:grid-cols-3' : 'md:grid-cols-3'}`}>
          {proofCases.map((item) => (
            <Link
              key={item.slug}
              to={`/case-studies#${item.slug}`}
              className="group overflow-hidden rounded-lg border border-slate-700 bg-slate-950 transition-colors hover:border-primary-400/70"
            >
              <div className="grid grid-cols-[1.25fr_0.85fr] gap-2 border-b border-slate-700 bg-slate-900 p-3">
                <figure>
                  <div className="overflow-hidden rounded-md bg-slate-800">
                    <img
                      src={item.sourceImage}
                      alt={item.sourceAlt}
                      loading="lazy"
                      className="aspect-[4/3] h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.03]"
                    />
                  </div>
                  <figcaption className="mt-2 text-xs font-medium text-slate-400">Original page</figcaption>
                </figure>

                <figure>
                  <div className="overflow-hidden rounded-md bg-white">
                    <img
                      src={item.certificateImage}
                      alt={item.certificateAlt}
                      loading="lazy"
                      className="aspect-[4/3] h-full w-full object-contain"
                    />
                  </div>
                  <figcaption className="mt-2 text-xs font-medium text-slate-400">Certificate</figcaption>
                </figure>
              </div>

              <div className="p-5">
                <div className="flex items-center gap-2">
                  <FileSearch className="h-4 w-4 text-primary-300" strokeWidth={iconStroke} />
                  <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                </div>
                <p className="mt-3 text-sm leading-6 text-slate-300">{item.challenge}</p>
                <div className="mt-4 flex gap-2 rounded-md bg-slate-900/80 text-sm leading-6 text-slate-200">
                  <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-green-400" strokeWidth={iconStroke} />
                  <span>{item.result}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
